
import { utils } from '@ims/1edtech-frontend-common';
import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';
import qs from 'qs';
import * as R from 'ramda';

import organizations from 'lib/records/modules/organizations';
import users from 'lib/records/modules/users';
import { mergeOrReplaceEntities } from 'lib/records/workflows/entities';
import store from 'lib/store';
import { setApplicationProperties } from 'domains/application/ducks/application';
import IOrg from 'domains/orgs/models/IOrg';
import IUser from 'domains/users/models/IUser';

import {
  DEFAULT_REQUEST_TIMEOUT,
  FREEMIUM_LOCKED_OUT_MESSAGE,
  ORG_DENIED_LOGIN_MESSAGE,
  TOKENS_STORAGE_KEY,
} from './constants';
import { IApiResponse } from './models';
import { getApiUrl, getImsxDescription } from './utils';
import { getAccessToken, setTokens } from './utils/tokens';

const getResponseStatus = R.pathOr<number>(0, ['status']);

const isUnauthenticated = R.compose<AxiosResponse, number, boolean>(R.equals(401), getResponseStatus);

const getResponseStructure = (response: AxiosResponse, success: boolean) =>
  R.compose<AxiosResponse, IApiResponse>(R.assoc('success', success))(response);

const formatResponse = (response: IApiResponse) => {
  return getResponseStructure(response.response || response, response.success);
};

const getRawResponse = (response: any) => response.response || response;

let refreshPromise: any = null;

const api = axios.create({
  timeout: DEFAULT_REQUEST_TIMEOUT,
  transformResponse: [
    // @ts-ignore
    ...axios.defaults.transformResponse,
    (response: AxiosResponse) => camelizeKeys(response),
  ],
  // TODO: replace this -> paramsSerializer: (params: any) => buildURL('', decamelizeKeys(params), null).slice(1),
  paramsSerializer: (params: any) => qs.stringify(decamelizeKeys(params)),
});

api.interceptors.response.use(R.assoc('success', true), R.assoc('success', false));

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const accessToken = getAccessToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

const refreshTokens = async (token: string) => {
  const response = (await api.post(getApiUrl('POST', 'oauth2server/refreshtoken'), {
    refresh_token: token,
  })) as IApiResponse;

  if (!isUnauthenticated(response)) {
    setTokens(response);
    const meResponse = await performRequest({
      url: 'login',
      method: 'GET',
    });

    if (meResponse.success) {
      const me = await mergeOrReplaceEntities(false, users, meResponse.data, false);
      store.dispatch(setApplicationProperties({ me }));
    }
  }

  return formatResponse(response);
};

interface IPerformRequestOptions {
  method: string;
  url: string;
  data?: any;
  params?: any;
  headers?: any;
  timeout?: number;
  returnRawResponse?: boolean;
  config?: any;
}

export const performRequest = async (options: IPerformRequestOptions, isRetry = false): Promise<IApiResponse> => {
  const {
    method,
    url,
    data = {},
    params = {},
    headers = {},
    timeout = DEFAULT_REQUEST_TIMEOUT,
    returnRawResponse = false,
    config = {},
  } = options;

  const tokens = utils.localStorage.getLocalItem(TOKENS_STORAGE_KEY);
  const { refreshToken = false } = tokens || {};
  const requestHeaders = R.compose(
    (mutableHeaders: any) => (R.isEmpty(headers) ? mutableHeaders : { ...mutableHeaders, ...headers }),
    (mutableHeaders: any) => mutableHeaders,
  )({});

  const defaultRequestOptions = {
    method,
    url: getApiUrl(method, url),
    headers: requestHeaders,
    timeout,
    ...config,
  };

  const requestOptions = R.compose(
    (mutlableOptions: any) => (R.isEmpty(params) ? mutlableOptions : R.assoc('params', params, mutlableOptions)),
    (mutlableOptions: any) => (R.isEmpty(data) ? mutlableOptions : R.assoc('data', data, mutlableOptions)),
  )(defaultRequestOptions);

  const axiosResponse = await api.request(requestOptions as AxiosRequestConfig);
  let response = axiosResponse as IApiResponse;

  if (response.response) {
    response = R.assoc('success', response.success, response.response);
  }

  const unAuthResponse = isUnauthenticated(response);
  const imsxDescription = getImsxDescription(response);
  let lockedOut: false | string = false;
  switch (imsxDescription) {
    case FREEMIUM_LOCKED_OUT_MESSAGE:
      lockedOut = 'freemiumLoginDenied';
      break;
    case ORG_DENIED_LOGIN_MESSAGE:
      lockedOut = 'orgLoginDenied';
      break;
    default:
      break;
  }
  const finalResponse = returnRawResponse ? getRawResponse(response) : formatResponse(response);

  if (unAuthResponse && utils.hasValue(lockedOut)) {
    const lockedOutUser = R.pathOr({}, ['data', 'errors', 0, 'user'], response) as IUser;
    const lockedOutOrg = R.pathOr({}, ['data', 'errors', 0, 'organization'], response) as IOrg;

    // Important to set user/org entities before setting locked out
    mergeOrReplaceEntities(true, users, lockedOutUser);
    mergeOrReplaceEntities(true, organizations, lockedOutOrg);
    store.dispatch(
      setApplicationProperties({
        lockedOut,
        me: lockedOutUser.id,
      }),
    );

    return finalResponse;
  }

  if (unAuthResponse && !isRetry) {
    if (refreshToken) {
      if (!refreshPromise) {
        refreshPromise = refreshTokens(refreshToken);
      }

      if (refreshPromise) {
        await refreshPromise;
        refreshPromise = null;
      }

      return performRequest(
        {
          method,
          url,
          data,
          params,
          headers,
        },
        true,
      );
    }

    // Otherwise log them out
    if (unAuthResponse) {
      store.dispatch(setApplicationProperties({ me: false }));
    }
  }

  return finalResponse;
};
